import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { formatApiError, authFetch } from '../utils/api';
import { Mail, ArrowLeft } from 'lucide-react';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const { language } = useLanguage();

  const t = language === 'el' ? {
    title: 'Ξεχάσατε τον κωδικό;',
    subtitle: 'Εισάγετε το email σας για να λάβετε σύνδεσμο επαναφοράς.',
    email: 'Email',
    submit: 'Αποστολή',
    submitting: 'Αποστολή...',
    success: 'Αν το email υπάρχει, θα λάβετε σύνδεσμο επαναφοράς.',
    back: 'Επιστροφή στη Σύνδεση'
  } : {
    title: 'Forgot Password?',
    subtitle: 'Enter your email to receive a password reset link.',
    email: 'Email',
    submit: 'Send Reset Link',
    submitting: 'Sending...',
    success: 'If the email exists, a reset link has been sent.',
    back: 'Back to Log In'
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setStatus('submitting');
    try {
      const res = await authFetch('/api/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw data;
      }
      setStatus('success');
    } catch (err) {
      setError(formatApiError(err.detail));
      setStatus('idle');
    }
  };

  return (
    <div className="min-h-screen bg-[#050811] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center text-white/60 hover:text-white text-sm mb-8 transition-colors">
          <ArrowLeft size={16} className="mr-2" /> {t.back}
        </Link>

        <div className="bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl p-8">
          <h1 data-testid="forgot-title" className="text-3xl font-bold text-white mb-2 text-center">{t.title}</h1>
          <p className="text-white/60 text-sm text-center mb-8">{t.subtitle}</p>

          {status === 'success' ? (
            <p data-testid="forgot-success" className="text-emerald-400 text-center">{t.success}</p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="relative">
                <Mail size={18} className="absolute left-3.5 top-3.5 text-white/40" />
                <input data-testid="forgot-email" type="email" required placeholder={t.email} value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg pl-11 pr-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-cyan-400/50 focus:ring-1 focus:ring-cyan-400/30 transition-colors" />
              </div>
              {error && <p data-testid="forgot-error" className="text-red-400 text-sm text-center">{error}</p>}
              <Button data-testid="forgot-submit" type="submit" disabled={status === 'submitting'} className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white" size="lg">
                {status === 'submitting' ? t.submitting : t.submit}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
